import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckBox, Form, Heading, Layer, Text } from 'grommet';
import { Ref, useEffect, useRef, useState } from 'preact/compat';
import { batch, useDispatch } from 'react-redux';

import { PrimaryButton } from '@/components/Buttons';
import ImageSelector from '@/components/ImageSelector';
import Header from '@/components/modals/Header';
import useInputField, { dateTimeLocalFormatter } from '@/hooks/useInputField';
import { useLatestActiveTime } from '@/hooks/useLatestActiveDateTime';
import { updateMediaInQueryClient, updateVideoMutationFn } from '@/queries/media';
import { Video } from '@/queries/media/types';
import { getInfiniteAdminPlaylistsQueryOptions } from '@/queries/playlists';
import { useSelector } from '@/redux/helper';
import { updateAlbumData } from '@/redux/slices/myAlbum/actions';
import { updateHabitatAlbumData } from '@/routes/habitat/components/Album/actions';
import { buildURL, get } from '@/shared/fetch';

import { updateShareModalMediaData } from '../ShareModal/shareModalSlice/actions';
import PlaylistSelector from './PlaylistSelector';

export interface EditVideoModalProps {
  videoId: string;
  defaultThumbnailURL?: string;
  defaultTitle?: string;
  canEditPlaylists?: boolean;
  defaultPublishedAt?: string;
  onClose?: () => void;
  onUpdate?: (data: Video) => void;
}

const CONSTRAINTS = { acceptedFormats: ['image/*'] } as const;
const MEDIA_TRANSFORM = {
  maxWidth: 640,
  maxHeight: 360,
  forceAspectRatio: [16, 9] as [number, number],
} as const;

const RECOMMENDED_SETTINGS = {
  ...MEDIA_TRANSFORM,
  fileSize: 1_000_000,
} as const;

const EditVideoModal = ({
  videoId,
  defaultThumbnailURL,
  defaultTitle,
  canEditPlaylists,
  defaultPublishedAt,
  onClose,
  onUpdate,
}: EditVideoModalProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [thumbnailURL, setThumbnailURL] = useState(defaultThumbnailURL);
  const [overlayThumbnail, setOverlayThumbnail] = useState(false);
  const [title, setTitle] = useState(defaultTitle);
  const mediaPreviewRef = useRef<HTMLImageElement>(null);
  const [playlistIds, setPlaylistIds] = useState<string[]>([]);
  const [publishedAt, setPublishedAt] = useState<Date>(() =>
    defaultPublishedAt ? new Date(defaultPublishedAt) : new Date(),
  );
  const publishedAtField = useInputField<Date>({
    value: publishedAt,
    formatter: dateTimeLocalFormatter,
    commitOn: 'change',
    onCommit: (date) => {
      if (date) {
        setPublishedAt(date);
      }
    },
  });

  const isAdmin = useSelector((state) => state.user.role === 'admin');
  const [now] = useLatestActiveTime();
  const isPublished = publishedAt.getTime() <= now;

  const { data } = useInfiniteQuery({
    ...getInfiniteAdminPlaylistsQueryOptions({ videos: [videoId] }),
    enabled: isAdmin && !!videoId,
  });

  const { mutate, isPending, error, isSuccess } = useMutation({
    mutationFn: updateVideoMutationFn,
    onSuccess: async () => {
      const { media } = await get(buildURL(`/media/${videoId}`));
      const payload = { id: videoId, data: media };
      batch(() => {
        dispatch(updateShareModalMediaData(media));
        dispatch(updateHabitatAlbumData(payload));
        dispatch(updateAlbumData(payload));
      });
      updateMediaInQueryClient(queryClient, videoId, media, 'video');
      onUpdate?.(media);
      onClose?.();
    },
  });

  const onSubmit = () => {
    mutate({
      _id: videoId,
      thumbnailURL,
      title,
      overlayThumbnail,
      playlists: isAdmin ? playlistIds : undefined,
      publishedAt: isPublished ? undefined : publishedAt.toISOString(),
    });
  };

  useEffect(() => {
    if (data?.list) {
      setPlaylistIds(data.list.map((p) => p._id));
    }
  }, [data]);

  return (
    <Layer onClickOutside={isPending ? undefined : onClose}>
      <div className="flex max-h-[90svh] w-[90vw] max-w-[420px] flex-col">
        <Header onClose={isPending ? undefined : onClose}>Edit Video</Header>

        <div className="grow overflow-auto">
          <div className="p-4">
            <Form onSubmit={onSubmit} className="flex flex-col gap-6">
              <div>
                <Heading margin="0" level="5">
                  Title:
                </Heading>
                <input
                  placeholder="write a short description"
                  value={title}
                  maxLength={100}
                  onChange={({ currentTarget: { value } }) => setTitle(value)}
                  className="preflight preflight-input w-full border-b pb-1 pt-2 text-xl font-bold placeholder:text-xs placeholder:text-grey-4"
                />
              </div>

              {isAdmin && canEditPlaylists && (
                <div>
                  <Heading margin="0" level="5">
                    Playlists:
                  </Heading>
                  <PlaylistSelector multiple value={playlistIds} onChange={setPlaylistIds} />
                </div>
              )}

              <div>
                <Heading margin={{ top: '0', bottom: '5px' }} level="5">
                  Video Thumbnail:
                </Heading>

                {thumbnailURL && (
                  <div className="aspect-video w-full bg-black">
                    <img
                      ref={mediaPreviewRef as Ref<HTMLImageElement>}
                      src={thumbnailURL}
                      alt=""
                      className="preflight preflight-img"
                    />
                  </div>
                )}

                <ImageSelector
                  url={thumbnailURL}
                  previewRef={mediaPreviewRef}
                  placeholder="https://"
                  constraints={CONSTRAINTS}
                  recommendedSettings={RECOMMENDED_SETTINGS}
                  transform={MEDIA_TRANSFORM}
                  filenameSuffix="thumb"
                  onChange={(value) => setThumbnailURL(value)}
                />
              </div>

              <div>
                <CheckBox
                  checked={overlayThumbnail}
                  name="overlayThumbnail"
                  label={
                    <span>
                      Add{' '}
                      <a
                        href="https://zl-media-recordings.s3.us-east-1.amazonaws.com/overlay.png"
                        target="_blank"
                        rel="noreferrer"
                        className="preflight preflight-a inline underline"
                      >
                        default overlay
                      </a>{' '}
                      (the play button) to the thumbnail
                    </span>
                  }
                  onChange={(event) => setOverlayThumbnail(event.currentTarget.checked)}
                />
              </div>

              {!isPublished && (
                <div>
                  <Heading margin="0" level="5">
                    Published At:
                  </Heading>
                  <p className="preflight preflight-p text-xs text-grey-3">
                    The scheduled notification or chat will be re-scheduled to the new time.
                  </p>
                  <input
                    required
                    name="publishedAt"
                    type="datetime-local"
                    value={publishedAtField.value}
                    onFocus={publishedAtField.onFocus}
                    onChange={publishedAtField.onChange}
                    onBlur={publishedAtField.onBlur}
                    className="preflight preflight-input w-full border-b pb-1 pt-2 text-xl font-bold placeholder:text-xs placeholder:text-grey-4"
                  />
                </div>
              )}

              {error && (
                <div className="self-center">
                  <Text color="red">{error}</Text>
                </div>
              )}

              {isSuccess && (
                <div className="self-center">
                  <Text color="green">Success</Text>
                </div>
              )}

              <div className="self-center">
                <PrimaryButton
                  className=""
                  loading={isPending}
                  type={isSuccess ? 'button' : 'submit'}
                  disabled={!title || !thumbnailURL || isPending}
                  size="large"
                  onClick={isSuccess ? onClose : undefined}
                  label={isSuccess ? 'Close' : 'Submit'}
                />
              </div>
            </Form>
          </div>
        </div>
      </div>
    </Layer>
  );
};

export default EditVideoModal;
